import { AnalyticCard } from '../AnalyticCard/AnalyticCard';
import { Box, Button, Stack } from '@mui/material';
import {
  CHART_DATA_IDS,
  ReportsPageInvoiceStatusChart,
} from '../../../../reports/reports-page/reports-page-components/invoice-status/ReportsPageInvoiceStatusChart';
import { useReportsInvoicesSummary } from '../../../../reports/reports-page/reports-page-hooks/reports-invoices-summary';
import { SheetWithDollarNew } from '@vy/ui-design-system';
import { AnalyticCardEmptyState } from '../../EmptyState/AnalyticCardEmptyState';
import { vyDate } from '@vy/date';
import { notDefined } from '@vy/null';
import { withDefault } from '@vy/utils';
import { getSelectorAttribute, homePageWidgetsSelectors } from '@vy/app-selectors';
import { useInvoiceStatistics } from '../../../../../hooks/requests/use-invoices-statistics';
import { useInvoicesBreakdownStats } from '../../../../../hooks/requests/use-invoices-breakdown-stats';
import { DEFAULT_CURRENCY, type Period } from '@vy/types';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatPriceToAbbreviated } from '../../../../../utils/formatters/formatPriceToAbbreviated';
import { VyDateRangeValue } from '../../../../../components/new-design/date-picker';
import { AnalyticMetricV2 } from '../AnalyticMetric/AnalyticMetricV2';
import { InvoicesBreakdown } from './InvoicesBreakdown';

const chartColorMapping = {
  [CHART_DATA_IDS.PAID_PRICE_ID]: '#A7EBB7',
  [CHART_DATA_IDS.OPEN_PRICE_ID]: '#B0D2FF',
  [CHART_DATA_IDS.PENDING_PRICE_ID]: '#FADEAE',
  [CHART_DATA_IDS.OVERDUE_PRICE_ID]: '#FF7D93',
};

export const InvoicesAnalyticsCard = () => {
  const {
    data: { invoicesSummary, countsByBillingStatus = {} },
    isLoading: isSummaryLoading,
  } = useReportsInvoicesSummary({ suspense: false });

  const { data: invoicesStatistics } = useInvoiceStatistics(undefined, { suspense: false });

  const [dateRange, setDateRange] = useState<VyDateRangeValue>({ start: null, end: null });

  const breakdownPeriod = useMemo<Period | undefined>(() => {
    if (notDefined(dateRange.start)) {
      return undefined;
    }

    return {
      startTime: dateRange.start,
      endTime: dateRange.end ?? vyDate().endOf('day').toDate(),
    };
  }, [dateRange]);

  const { data: invoicesBreakdownStats } = useInvoicesBreakdownStats(breakdownPeriod, {
    suspense: false,
  });

  const breakdown = withDefault(invoicesBreakdownStats, {
    total: { sum: 0, count: 0 },
    inReview: { sum: 0, count: 0 },
    inOverage: { sum: 0, count: 0 },
  });

  const overdueAmount = withDefault(invoicesStatistics?.summary.overdueAmount, 0);
  const pendingPaymentAmount = withDefault(invoicesStatistics?.summary.pendingPaymentAmount, 0);
  const totalOutstanding = overdueAmount + pendingPaymentAmount;

  const totalCount = useMemo(() => {
    const billingStatusAmounts = Object.values(countsByBillingStatus);

    return billingStatusAmounts.reduce((total, amount) => total + amount, 0);
  }, [countsByBillingStatus]);

  const isNoData = [
    invoicesSummary?.None,
    invoicesSummary?.Overdue,
    invoicesSummary?.Paid,
    invoicesSummary?.PendingPayment,
  ].every((metric) => metric === undefined || metric === 0);

  const formatter = (value: number) => formatPriceToAbbreviated(DEFAULT_CURRENCY, value);

  return (
    <AnalyticCard
      title="Invoices"
      dataVy={homePageWidgetsSelectors.invoicesWidget}
      isLoading={isSummaryLoading}
      renderAdditionalHeaderContent={() => (
        <Button
          component={Link}
          to="/invoices/overview"
          variant="text"
          sx={{ color: '#0521FF' }}
          size="small"
          {...getSelectorAttribute(homePageWidgetsSelectors.invoicesWidgetViewInvoices)}
        >
          View invoices
        </Button>
      )}
    >
      {isNoData ? (
        <AnalyticCardEmptyState
          title="No invoices yet"
          description="create one to start tracking payments."
          icon={<SheetWithDollarNew />}
        />
      ) : (
        <Stack
          direction="row"
          sx={{
            gap: 3,
            alignItems: "center"
          }}>
          <Box {...getSelectorAttribute(homePageWidgetsSelectors.invoicesWidgetChart)}>
            <ReportsPageInvoiceStatusChart
              overduePrice={overdueAmount}
              pendingPrice={pendingPaymentAmount}
              totalInvoicesCount={totalCount}
              width={195}
              height={195}
              outerRadiusMultiplier={0.45}
              colorMapping={chartColorMapping}
              centerContent={
                <AnalyticMetricV2
                  titleText="Total Outstanding"
                  value={totalOutstanding}
                  formatter={formatter}
                  valueFontSize={16}
                  boldTitle={false}
                  centeredValueAlignment
                  titleSx={{ marginBottom: '6px', fontSize: '12px' }}
                  dataVy={homePageWidgetsSelectors.invoicesWidgetTotalOutstanding}
                />
              }
            />
          </Box>
          <Box sx={{
            flex: 1
          }}>
            <InvoicesBreakdown
              totalAmount={breakdown.total.sum}
              inReviewCount={breakdown.inReview.count}
              inReviewAmount={breakdown.inReview.sum}
              inOverageCount={breakdown.inOverage.count}
              inOverageAmount={breakdown.inOverage.sum}
              formatter={formatter}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              totalAmountDataVy={homePageWidgetsSelectors.invoicesWidgetBreakdownTotal}
              inReviewDataVy={homePageWidgetsSelectors.invoicesWidgetBreakdownInReview}
              inOverageDataVy={homePageWidgetsSelectors.invoicesWidgetBreakdownInOverage}
            />
          </Box>
        </Stack>
      )}
    </AnalyticCard>
  );
};
